import { useState, useEffect, useRef, useCallback } from "react";

// Constants
const SCROLL_THRESHOLD = 5; // pixels
const SCROLL_BOTTOM_THRESHOLD = 10; // pixels
const MIN_SCROLL_HEIGHT = 100; // pixels
const CONTENT_CHECK_MAX_ATTEMPTS = 10;
const CONTENT_CHECK_INTERVAL = 200; // ms
const CONTENT_CHECK_INITIAL_DELAY = 100; // ms
const SCROLL_THROTTLE_DELAY = 100; // ms

interface UseScrollToBottomOptions {
  /**
   * Ref to the scrollable container element
   */
  containerRef: React.RefObject<HTMLDivElement>;
  /**
   * The hook will check scroll state when this value changes
   */
  contentReady?: number | string | boolean;
  /**
   * Optional key to reset scroll state when content changes
   */
  resetKey?: string;
}

/**
 * Hook to detect when user has scrolled to the bottom of a container.
 * Handles both scrollable and non-scrollable content.
 *
 * @param options - Configuration options
 * @returns Object with `isScrolledToBottom` state and `handleScroll` callback
 */
export function useScrollToBottom({
  containerRef,
  contentReady,
  resetKey,
}: UseScrollToBottomOptions) {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState<boolean>(false);
  const isScrolledToBottomRef = useRef<boolean>(false);
  const hasUserScrolledRef = useRef<boolean>(false);
  const initialScrollTopRef = useRef<number>(0);
  const initialScrollTopSetRef = useRef<boolean>(false);
  const previousResetKeyRef = useRef<string>("");
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset scroll state when resetKey changes
  useEffect(() => {
    const normalizedResetKey = resetKey || "";
    if (previousResetKeyRef.current !== normalizedResetKey) {
      setIsScrolledToBottom(false);
      isScrolledToBottomRef.current = false;
      hasUserScrolledRef.current = false;
      initialScrollTopRef.current = 0;
      initialScrollTopSetRef.current = false;
      previousResetKeyRef.current = normalizedResetKey;
    }
  }, [resetKey]);

  // Check if content fits in viewport (non-scrollable) when content is ready
  useEffect(() => {
    if (
      !containerRef.current ||
      contentReady === undefined ||
      contentReady === false
    ) {
      return;
    }

    const container = containerRef.current;

    // Wait for content to finish rendering before measuring
    // Use multiple checks with delays to ensure content is fully laid out
    let checkAttempts = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    let interactionHandlers: {
      click: () => void;
      wheel: () => void;
      touchstart: () => void;
    } | null = null;
    let initialScrollSet = false;

    const checkIfContentFits = () => {
      if (!containerRef.current) {
        return;
      }

      const currentContainer = containerRef.current;

      // Set initial scroll position only once per content load
      // This prevents overwriting if user scrolls during content rendering
      if (!initialScrollSet && !initialScrollTopSetRef.current) {
        initialScrollTopRef.current = currentContainer.scrollTop;
        initialScrollTopSetRef.current = true;
        initialScrollSet = true;
      }

      // Check if content fits in viewport (not scrollable)
      const scrollHeight = currentContainer.scrollHeight;
      const clientHeight = currentContainer.clientHeight;

      // If measurements are still 0 or very small, content hasn't rendered yet
      if (
        scrollHeight < MIN_SCROLL_HEIGHT &&
        checkAttempts < CONTENT_CHECK_MAX_ATTEMPTS
      ) {
        checkAttempts++;
        timeoutId = setTimeout(checkIfContentFits, CONTENT_CHECK_INTERVAL);
        return;
      }

      // If content fits in viewport (not scrollable), enable buttons after user interaction
      if (scrollHeight <= clientHeight + 1) {
        const handleUserInteraction = () => {
          hasUserScrolledRef.current = true;
          if (!isScrolledToBottomRef.current) {
            isScrolledToBottomRef.current = true;
            setIsScrolledToBottom(true);
          }
          // Remove listeners after first interaction
          if (interactionHandlers && container) {
            container.removeEventListener("click", interactionHandlers.click);
            container.removeEventListener("wheel", interactionHandlers.wheel);
            container.removeEventListener(
              "touchstart",
              interactionHandlers.touchstart
            );
            interactionHandlers = null;
          }
        };

        // Store handlers for cleanup
        interactionHandlers = {
          click: handleUserInteraction,
          wheel: handleUserInteraction,
          touchstart: handleUserInteraction,
        };

        // Listen for any user interaction (only if container still exists)
        if (container) {
          container.addEventListener("click", interactionHandlers.click, {
            once: true,
          });
          container.addEventListener("wheel", interactionHandlers.wheel, {
            once: true,
          });
          container.addEventListener(
            "touchstart",
            interactionHandlers.touchstart,
            {
              once: true,
            }
          );
        }
      }
    };

    // Start checking after a small initial delay to allow first render
    timeoutId = setTimeout(checkIfContentFits, CONTENT_CHECK_INITIAL_DELAY);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (interactionHandlers && container) {
        container.removeEventListener("click", interactionHandlers.click);
        container.removeEventListener("wheel", interactionHandlers.wheel);
        container.removeEventListener(
          "touchstart",
          interactionHandlers.touchstart
        );
      }
    };
  }, [containerRef, contentReady, resetKey]);

  // Throttled scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Throttle scroll checks
      scrollTimeoutRef.current = setTimeout(() => {
        // Use containerRef instead of e.currentTarget to avoid null reference issues
        const container = containerRef.current;
        if (!container) {
          return;
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        // Set initial scroll position on first scroll event if not already set
        // This handles cases where content is scrollable and browser restores scroll position
        if (!initialScrollTopSetRef.current) {
          initialScrollTopRef.current = scrollTop;
          initialScrollTopSetRef.current = true;
        }

        // Track if user has actually scrolled (not just at initial position)
        if (
          Math.abs(scrollTop - initialScrollTopRef.current) > SCROLL_THRESHOLD
        ) {
          hasUserScrolledRef.current = true;
        }

        // Check if scrolled to bottom (with threshold)
        const isAtBottom =
          scrollHeight - scrollTop <= clientHeight + SCROLL_BOTTOM_THRESHOLD;

        if (
          isAtBottom &&
          hasUserScrolledRef.current &&
          !isScrolledToBottomRef.current
        ) {
          isScrolledToBottomRef.current = true;
          setIsScrolledToBottom(true);
        }
      }, SCROLL_THROTTLE_DELAY);
    },
    [containerRef]
  );

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    isScrolledToBottom,
    handleScroll,
  };
}
